import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { marked } from "marked";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Button, Badge, Offcanvas, OffcanvasHeader, OffcanvasBody } from "reactstrap";
import { PostData, DownloadData } from "../../../services/FetchData";
import { ProductContext } from "../../../store";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import { format, startOfDay, endOfDay, parseISO, isValid } from "date-fns";

const SESSIONS_PAGE_SIZE = 30;

function toApiDateFrom(ymd) {
	if (!ymd || typeof ymd !== "string") return undefined;
	const d = parseISO(ymd);
	if (!isValid(d)) return undefined;
	return startOfDay(d).toISOString();
}

function toApiDateTo(ymd) {
	if (!ymd || typeof ymd !== "string") return undefined;
	const d = parseISO(ymd);
	if (!isValid(d)) return undefined;
	return endOfDay(d).toISOString();
}

// Parse assistant markdown to HTML (like conversionbox-chat-widget.js). Links open in new tab.
marked.setOptions({ breaks: true });
if (marked.Renderer) {
	const renderer = new marked.Renderer();
	renderer.link = function (href, title, text) {
		const h = (href || "").trim();
		const t = (title || "").trim();
		return '<a href="' + h + '" target="_blank" rel="noopener noreferrer"' + (t ? ' title="' + t + '"' : "") + ">" + (text || h) + "</a>";
	};
	marked.use({ renderer });
}

/** Remove only the first line when it is exactly "English" or "Spanish" (optional : - . and spaces), then newline(s). Do not remove any following content (e.g. intro paragraph). */
function stripDisplayLanguagePrefix(text) {
	if (!text || typeof text !== "string") return "";
	const s = String(text);
	const re = /^\s*(English|Spanish)\s*[:\-\.]?\s*[\r\n]+/i;
	if (re.test(s)) return s.replace(re, "").trimStart();
	return s;
}

/** Remove citation markers from AI response ([ID:62], [1], [], "(marked not used)") so they are not shown. Preserves newlines so markdown bullets stay on separate lines. */
function stripCitationIds(text) {
	if (!text) return "";
	let s = String(text)
		.replace(/\[\s*ID\s*:\s*\d+\s*\]/gi, "")
		// Use \n so list items after [1], [] stay on their own line (bullets render)
		.replace(/\s*\[\d*\]\s*/g, "\n")
		.replace(/\s*\[\s*\]\s*/g, "\n")
		.replace(/\s*\(\s*marked\s+not\s+used\s*\)\s*/gi, "\n")
		.replace(/\s*marked\s+not\s+used\s*/gi, "\n");
	return s.replace(/[ \t]+/g, " ").trim();
}

function parseAssistantMarkdown(text) {
	if (!text || typeof text !== "string") return "";
	const stripped = stripCitationIds(stripDisplayLanguagePrefix(text));
	try {
		let html = marked.parse(stripped);
		// Ensure links open in new tab if custom renderer wasn't used
		if (typeof html === "string") {
			html = html.replace(/<a (?![^>]*target=)/gi, "<a target=\"_blank\" rel=\"noopener noreferrer\" ");
		}
		return html;
	} catch (e) {
		return text;
	}
}

function contactInitials(contact) {
	if (!contact) return null;
	const first = String(contact.firstName || "").trim();
	const last = String(contact.lastName || "").trim();
	if (first && last) return (first[0] + last[0]).toUpperCase();
	if (first.length >= 2) return first.slice(0, 2).toUpperCase();
	if (first) return first[0].toUpperCase();
	return null;
}

function contactDisplayName(contact) {
	if (!contact) return "";
	const first = String(contact.firstName || "").trim();
	const last = String(contact.lastName || "").trim();
	return [first, last].filter(Boolean).join(" ");
}

const Conversations = () => {
	const { currentWebsiteId } = useContext(ProductContext);
	const websiteId = currentWebsiteId || Cookies.get("_website_id");

	const [sessionsLoader, setSessionsLoader] = useState(true);
	const [sessions, setSessions] = useState([]);
	const [selectedSession, setSelectedSession] = useState(null);
	const [messagesLoader, setMessagesLoader] = useState(false);
	const [messages, setMessages] = useState([]);
	const [sessionContact, setSessionContact] = useState(null);
	const [filterOpen, setFilterOpen] = useState(false);
	const [draftSearch, setDraftSearch] = useState("");
	const [draftRetrieval, setDraftRetrieval] = useState("");
	const [draftContactForm, setDraftContactForm] = useState("all");
	const [draftLanguage, setDraftLanguage] = useState("all");
	const [draftDateFrom, setDraftDateFrom] = useState("");
	const [draftDateTo, setDraftDateTo] = useState("");

	const [committedSearch, setCommittedSearch] = useState("");
	const [committedRetrieval, setCommittedRetrieval] = useState("");
	const [committedContactForm, setCommittedContactForm] = useState("all");
	const [committedLanguage, setCommittedLanguage] = useState("all");
	const [committedDateFrom, setCommittedDateFrom] = useState("");
	const [committedDateTo, setCommittedDateTo] = useState("");

	const sessionsListRef = useRef(null);
	const loadMoreSentinelRef = useRef(null);
	const sessionsNextOffsetRef = useRef(0);
	const sessionsLoadMoreInFlightRef = useRef(false);
	const [sessionsHasMore, setSessionsHasMore] = useState(false);
	const [sessionsLoadingMore, setSessionsLoadingMore] = useState(false);
	const [exporting, setExporting] = useState(false);

	const activeFilterCount =
		(committedSearch ? 1 : 0) +
		(committedRetrieval ? 1 : 0) +
		(committedContactForm !== "all" ? 1 : 0) +
		(committedLanguage !== "all" ? 1 : 0) +
		(committedDateFrom || committedDateTo ? 1 : 0);

	const openFilterPanel = useCallback(() => {
		setDraftSearch(committedSearch);
		setDraftRetrieval(committedRetrieval);
		setDraftContactForm(committedContactForm);
		setDraftLanguage(committedLanguage);
		setDraftDateFrom(committedDateFrom);
		setDraftDateTo(committedDateTo);
		setFilterOpen(true);
	}, [committedSearch, committedRetrieval, committedContactForm, committedLanguage, committedDateFrom, committedDateTo]);

	const applyFilters = useCallback(() => {
		setCommittedSearch(draftSearch.trim());
		setCommittedRetrieval(draftRetrieval.trim());
		setCommittedContactForm(draftContactForm);
		setCommittedLanguage(draftLanguage);
		setCommittedDateFrom(draftDateFrom.trim());
		setCommittedDateTo(draftDateTo.trim());
		setFilterOpen(false);
	}, [draftSearch, draftRetrieval, draftContactForm, draftLanguage, draftDateFrom, draftDateTo]);

	const clearFilters = useCallback(() => {
		setDraftSearch("");
		setDraftRetrieval("");
		setDraftContactForm("all");
		setDraftLanguage("all");
		setDraftDateFrom("");
		setDraftDateTo("");
		setCommittedSearch("");
		setCommittedRetrieval("");
		setCommittedContactForm("all");
		setCommittedLanguage("all");
		setCommittedDateFrom("");
		setCommittedDateTo("");
		setFilterOpen(false);
	}, []);

	const buildSessionsPayload = useCallback(
		(offset) => {
			const dateFromIso = toApiDateFrom(committedDateFrom);
			const dateToIso = toApiDateTo(committedDateTo);
			return {
				websiteId,
				//returnData: true,
				offset,
				pageSize: SESSIONS_PAGE_SIZE,
				...(committedSearch ? { q: committedSearch } : {}),
				...(committedRetrieval ? { retrievalSource: committedRetrieval } : {}),
				...(committedContactForm !== "all" ? { contactFormFilter: committedContactForm } : {}),
				...(committedLanguage !== "all" ? { languageFilter: committedLanguage } : {}),
				...(dateFromIso ? { dateFrom: dateFromIso } : {}),
				...(dateToIso ? { dateTo: dateToIso } : {}),
			};
		},
		[websiteId, committedSearch, committedRetrieval, committedContactForm, committedLanguage, committedDateFrom, committedDateTo]
	);

	const buildExportPayload = useCallback(
		(singleSession = null) => {
			if (singleSession?.sessionId) {
				return {
					websiteId,
					sessionId: singleSession.sessionId,
					...(singleSession.visitorId ? { visitorId: singleSession.visitorId } : {}),
				};
			}
			const dateFromIso = toApiDateFrom(committedDateFrom);
			const dateToIso = toApiDateTo(committedDateTo);
			return {
				websiteId,
				...(committedSearch ? { q: committedSearch } : {}),
				...(committedRetrieval ? { retrievalSource: committedRetrieval } : {}),
				...(committedContactForm !== "all" ? { contactFormFilter: committedContactForm } : {}),
				...(committedLanguage !== "all" ? { languageFilter: committedLanguage } : {}),
				...(dateFromIso ? { dateFrom: dateFromIso } : {}),
				...(dateToIso ? { dateTo: dateToIso } : {}),
			};
		},
		[
			websiteId,
			committedSearch,
			committedRetrieval,
			committedContactForm,
			committedLanguage,
			committedDateFrom,
			committedDateTo,
		]
	);

	const triggerXlsxDownload = useCallback((arrayBuffer, filename) => {
		const blob = new Blob([arrayBuffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);
		link.setAttribute("download", filename);
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}, []);

	const runBulkExportEmail = useCallback(async () => {
		if (!websiteId) {
			toast.error("Website ID is missing");
			return;
		}
		setExporting(true);
		try {
			const payload = buildExportPayload();
			const response = await PostData(
				"ms4",
				"api/v1/cmsAssistant/chat/exportConversationsXlsx",
				payload
			);
			if (response?.status === "success" && response?.data?.delivery === "email") {
				toast.success("Export started. We will email you when the file is ready (link valid 24 hours).");
				return;
			}
			if (response?.message) {
				throw new Error(response.message);
			}
			throw new Error("Unexpected response from export");
		} catch (error) {
			console.error("Error starting export:", error);
			toast.error(error.message || "Failed to start export. Please try again.");
		} finally {
			setExporting(false);
		}
	}, [websiteId, buildExportPayload]);

	const runSingleConversationDownload = useCallback(
		async (payload, filenamePrefix) => {
			if (!websiteId) {
				toast.error("Website ID is missing");
				return;
			}
			setExporting(true);
			try {
				const data = await DownloadData(
					"ms4",
					"api/v1/cmsAssistant/chat/exportConversationsXlsx",
					payload
				);
				if (!(data instanceof ArrayBuffer)) {
					throw new Error("Unexpected response from export");
				}
				const filename = `${filenamePrefix}_${format(new Date(), "yyyy-MM-dd_HH-mm-ss")}.xlsx`;
				triggerXlsxDownload(data, filename);
				toast.success("Export downloaded");
			} catch (error) {
				console.error("Error exporting conversations:", error);
				toast.error(error.message || "Failed to export. Please try again.");
			} finally {
				setExporting(false);
			}
		},
		[websiteId, triggerXlsxDownload]
	);

	const handleExportFiltered = useCallback(() => {
		runBulkExportEmail();
	}, [runBulkExportEmail]);

	const handleExportSelectedSession = useCallback(() => {
		if (!selectedSession?.sessionId) {
			toast.error("Select a conversation to export");
			return;
		}
		runSingleConversationDownload(buildExportPayload(selectedSession), "cms_assistant_conversation");
	}, [runSingleConversationDownload, buildExportPayload, selectedSession]);

	// Initial / filter-change fetch (page 0)
	useEffect(() => {
		if (!websiteId) {
			setSessionsLoader(false);
			return;
		}
		sessionsNextOffsetRef.current = 0;
		const fetchSessions = async () => {
			setSessionsLoader(true);
			setSessionsHasMore(false);
			try {
				const payload = buildSessionsPayload(0);
				const response = await PostData("ms4", "api/v1/cmsAssistant/chat/sessionsForWebsite", payload);
				if (response?.status === "success" && Array.isArray(response?.data?.sessions)) {
					const list = response.data.sessions;
					sessionsNextOffsetRef.current =
						typeof response.data.nextOffset === "number" ? response.data.nextOffset : list.length;
					setSessionsHasMore(Boolean(response.data.hasMore));
					setSessions(list);
					if (list.length > 0) {
						setSelectedSession((prev) => {
							if (!prev || !list.some((s) => s.sessionId === prev.sessionId)) {
								return list[0];
							}
							return prev;
						});
					} else {
						setSelectedSession(null);
					}
				} else {
					setSessions([]);
					setSelectedSession(null);
					setSessionsHasMore(false);
				}
			} catch (error) {
				console.error("Error fetching sessions:", error);
				toast.error("Failed to load conversations");
				setSessions([]);
				setSelectedSession(null);
				setSessionsHasMore(false);
			} finally {
				setSessionsLoader(false);
			}
		};
		fetchSessions();
	}, [websiteId, committedSearch, committedRetrieval, committedContactForm, committedLanguage, committedDateFrom, committedDateTo, buildSessionsPayload]);

	const loadMoreSessions = useCallback(async () => {
		if (!websiteId || sessionsLoadingMore || !sessionsHasMore || sessionsLoader) return;
		if (sessionsLoadMoreInFlightRef.current) return;
		sessionsLoadMoreInFlightRef.current = true;
		setSessionsLoadingMore(true);
		try {
			const offset = sessionsNextOffsetRef.current;
			const payload = buildSessionsPayload(offset);
			const response = await PostData("ms4", "api/v1/cmsAssistant/chat/sessionsForWebsite", payload);
			if (response?.status === "success" && Array.isArray(response?.data?.sessions)) {
				const list = response.data.sessions;
				sessionsNextOffsetRef.current =
					typeof response.data.nextOffset === "number" ? response.data.nextOffset : offset + list.length;
				setSessionsHasMore(Boolean(response.data.hasMore));
				setSessions((prev) => {
					const seen = new Set(prev.map((s) => s.sessionId));
					const merged = [...prev];
					for (const s of list) {
						if (s?.sessionId && !seen.has(s.sessionId)) {
							seen.add(s.sessionId);
							merged.push(s);
						}
					}
					return merged;
				});
			}
		} catch (error) {
			console.error("Error loading more sessions:", error);
			toast.error("Failed to load more conversations");
		} finally {
			sessionsLoadMoreInFlightRef.current = false;
			setSessionsLoadingMore(false);
		}
	}, [websiteId, sessionsLoadingMore, sessionsHasMore, sessionsLoader, buildSessionsPayload]);

	// Load next page when the list footer is visible (covers short lists that do not scroll)
	useEffect(() => {
		const scrollRoot = sessionsListRef.current;
		const sentinel = loadMoreSentinelRef.current;
		if (!scrollRoot || !sentinel || !sessionsHasMore || sessionsLoader) return undefined;

		const obs = new IntersectionObserver(
			(entries) => {
				if (!entries[0]?.isIntersecting) return;
				if (sessionsLoader || sessionsLoadingMore || !sessionsHasMore) return;
				loadMoreSessions();
			},
			{ root: scrollRoot, rootMargin: "80px", threshold: 0 }
		);
		obs.observe(sentinel);
		return () => obs.disconnect();
	}, [sessionsLoader, sessionsLoadingMore, sessionsHasMore, loadMoreSessions, sessions.length]);

	// Fetch messages when a session is selected
	useEffect(() => {
		if (!websiteId || !selectedSession?.sessionId) {
			setMessages([]);
			setSessionContact(null);
			return;
		}
		const fetchConversation = async () => {
			setMessagesLoader(true);
			try {
				const response = await PostData("ms4", "api/v1/cmsAssistant/chat/drupal/conversation", {
					websiteId,
					sessionId: selectedSession.sessionId,
					visitorId: selectedSession.visitorId || undefined,
				});
				if (response?.status === "success" && response?.data?.messages) {
					setMessages(response.data.messages);
					setSessionContact(response.data.contact || selectedSession.contact || null);
				} else {
					setMessages([]);
					setSessionContact(selectedSession.contact || null);
				}
			} catch (error) {
				console.error("Error fetching conversation:", error);
				toast.error("Failed to load conversation");
				setMessages([]);
				setSessionContact(selectedSession.contact || null);
			} finally {
				setMessagesLoader(false);
			}
		};
		fetchConversation();
	}, [websiteId, selectedSession?.sessionId, selectedSession?.visitorId, selectedSession?.contact]);

	const getInitials = (session) => {
		const fromContact = contactInitials(session?.contact);
		if (fromContact) return fromContact;
		if (session?.visitorId && String(session.visitorId).length >= 2) {
			return String(session.visitorId).slice(0, 2).toUpperCase();
		}
		return "?";
	};

	const activeContact = sessionContact || selectedSession?.contact || null;

	if (!websiteId) {
		return <DetailSkeleton />;
	}

	return (
		<div className="pageContent">
			<div className="pageHeader d-flex align-items-center justify-content-between flex-wrap gap-2">
				<h2 className="pageTitle mb-0">Conversations</h2>
				<div className="d-flex flex-wrap align-items-center gap-2">
					<Button
						color="primary"
						size="sm"
						className="d-inline-flex align-items-center gap-2"
						onClick={handleExportFiltered}
						disabled={exporting}
					>
						{exporting ? (
							<span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
						) : (
							<i className="fa fa-download" aria-hidden="true" />
						)}
						<span>Export filtered (XLSX)</span>
					</Button>
					<Button color="primary" outline size="sm" className="d-inline-flex align-items-center gap-2" onClick={openFilterPanel}>
						<i className="fa fa-filter" aria-hidden="true" />
						<span>Filter</span>
						{activeFilterCount > 0 ? (
							<Badge color="primary" pill className="ms-1">
								{activeFilterCount}
							</Badge>
						) : null}
					</Button>
				</div>
			</div>

			<Offcanvas direction="end" isOpen={filterOpen} toggle={() => setFilterOpen(false)} style={{ maxWidth: 400, width: "100%" }}>
				<OffcanvasHeader toggle={() => setFilterOpen(false)}>Filter conversations</OffcanvasHeader>
				<OffcanvasBody className="d-flex flex-column gap-3 pt-0">
					<div>
						<label className="form-label small text-muted mb-1">Message search</label>
						<input
							type="search"
							className="form-control form-control-sm"
							placeholder="Words in any message…"
							value={draftSearch}
							onChange={(e) => setDraftSearch(e.target.value)}
							aria-label="Search conversation messages"
						/>
					</div>
					<div>
						<label className="form-label small text-muted mb-1">Source</label>
						<input
							type="search"
							className="form-control form-control-sm"
							placeholder="e.g. Parkinson, page title…"
							value={draftRetrieval}
							onChange={(e) => setDraftRetrieval(e.target.value)}
							aria-label="Filter by knowledge base source title"
						/>
					</div>
					<div>
						<label className="form-label small text-muted mb-1">Contact form</label>
						<select
							className="form-select form-select-sm"
							value={draftContactForm}
							onChange={(e) => setDraftContactForm(e.target.value)}
							aria-label="Filter by contact form submission"
						>
							<option value="all">All conversations</option>
							<option value="submitted">Contact form submitted</option>
							<option value="not_submitted">No contact form</option>
						</select>
					</div>
					<div>
						<label className="form-label small text-muted mb-1">Language</label>
						<select
							className="form-select form-select-sm"
							value={draftLanguage}
							onChange={(e) => setDraftLanguage(e.target.value)}
							aria-label="Filter by conversation language"
						>
							<option value="all">All languages</option>
							<option value="en">English</option>
							<option value="es">Spanish</option>
						</select>
					</div>
					<div>
						<label className="form-label small text-muted mb-1">Date range (message time)</label>
						<div className="d-flex flex-wrap gap-2 align-items-center">
							<input
								type="date"
								className="form-control form-control-sm"
								style={{ minWidth: 0, flex: "1 1 120px" }}
								value={draftDateFrom}
								onChange={(e) => setDraftDateFrom(e.target.value)}
								aria-label="From date"
							/>
							<span className="small text-muted">to</span>
							<input
								type="date"
								className="form-control form-control-sm"
								style={{ minWidth: 0, flex: "1 1 120px" }}
								value={draftDateTo}
								onChange={(e) => setDraftDateTo(e.target.value)}
								aria-label="To date"
							/>
						</div>
						<p className="small text-muted mb-0 mt-1">Shows sessions with at least one message in this range (local day boundaries).</p>
					</div>
					<div className="d-flex flex-wrap gap-2 mt-auto pt-2 border-top">
						<Button color="primary" size="sm" onClick={applyFilters}>
							Apply filters
						</Button>
						<Button color="link" size="sm" className="text-decoration-none px-2" onClick={clearFilters}>
							Clear all
						</Button>
					</div>
				</OffcanvasBody>
			</Offcanvas>

			<div
				style={{
					display: "flex",
					height: "calc(100vh - 180px)",
					minHeight: 400,
					border: "1px solid #dee2e6",
					overflow: "hidden",
					backgroundColor: "#fff",
				}}
				className="chat-container"
			>
				{/* Left: conversation list (grouped by session) */}
				<div
					style={{
						width: 320,
						minWidth: 320,
						borderRight: "1px solid #dee2e6",
						display: "flex",
						flexDirection: "column",
						backgroundColor: "#f8f9fa",
					}}
				>
					{sessionsLoader ? (
						<div style={{ overflowY: "auto", flex: 1, padding: "8px 0" }}>
							{[1, 2, 3, 4, 5].map((i) => (
								<div
									key={i}
									className="d-flex align-items-start gap-2"
									style={{ padding: "12px 14px", borderBottom: "1px solid #eee" }}
								>
									<Skeleton circle width={40} height={40} />
									<div style={{ flex: 1, minWidth: 0 }}>
										<Skeleton height={14} count={2} style={{ marginBottom: 4 }} />
										<Skeleton height={11} width={60} />
									</div>
								</div>
							))}
						</div>
					) : sessions.length === 0 ? (
						<div className="p-4 text-muted text-center small">
							{committedSearch ||
							committedRetrieval ||
							committedContactForm !== "all" ||
							committedDateFrom ||
							committedDateTo
								? "No conversations match your filters"
								: "No conversations yet"}
						</div>
					) : (
						<div ref={sessionsListRef} style={{ overflowY: "auto", flex: 1 }}>
							{sessions.map((session) => {
								const isSelected =
									selectedSession && selectedSession.sessionId === session.sessionId;
								return (
									<div
										key={session.sessionId}
										onClick={() => setSelectedSession(session)}
										style={{
											padding: "12px 14px",
											borderBottom: "1px solid #eee",
											cursor: "pointer",
											backgroundColor: isSelected ? "#e7f1ff" : "transparent",
										}}
									>
										<div className="d-flex align-items-start gap-2">
											<div
												style={{
													width: 40,
													height: 40,
													borderRadius: "50%",
													background: "#5c50e6",
													color: "#fff",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													fontSize: 14,
													fontWeight: 600,
													flexShrink: 0,
												}}
											>
												{getInitials(session)}
											</div>
											<div style={{ flex: 1, minWidth: 0 }}>
												<div
													style={{
														fontSize: 13,
														fontWeight: 500,
														color: "#333",
														overflow: "hidden",
														textOverflow: "ellipsis",
														display: "-webkit-box",
														WebkitLineClamp: 2,
														WebkitBoxOrient: "vertical",
														lineHeight: 1.3,
													}}
												>
													{session.firstMessage || "No message"}
												</div>
												{contactDisplayName(session.contact) ? (
													<div
														style={{
															fontSize: 12,
															color: "#495057",
															marginTop: 2,
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
														}}
													>
														{contactDisplayName(session.contact)}
													</div>
												) : null}
												<div
													style={{
														fontSize: 11,
														color: "#adb5bd",
														marginTop: 4,
														display: "flex",
														alignItems: "center",
														gap: 6,
														flexWrap: "wrap",
													}}
												>
													<span>
														{session.dateTime
															? format(new Date(session.dateTime), "MM/dd/yyyy")
															: ""}
													</span>
													{session.contactFormSubmitted ? (
														<span
															className="badge rounded-pill"
															style={{
																backgroundColor: "#198754",
																fontSize: 10,
																fontWeight: 600,
															}}
														>
															Contact form
														</span>
													) : null}
												</div>
											</div>
										</div>
									</div>
								);
							})}
							<div ref={loadMoreSentinelRef} style={{ height: 1, width: "100%" }} aria-hidden />
							{sessionsLoadingMore ? (
								<div className="py-3 px-2 text-center text-muted small border-top">
									<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
									Loading more…
								</div>
							) : null}
							{sessionsHasMore && !sessionsLoadingMore ? (
								<div className="py-2 px-2 text-center text-muted" style={{ fontSize: 11 }}>
									More conversations load as you scroll
								</div>
							) : null}
						</div>
					)}
				</div>

				{/* Right: active conversation */}
				<div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
					{!selectedSession ? (
						<div
							className="d-flex align-items-center justify-content-center text-muted"
							style={{ flex: 1 }}
						>
							Select a conversation
						</div>
					) : (
						<>
							{/* Header: show first user question */}
							<div
								style={{
									padding: "12px 16px",
									borderBottom: "1px solid #dee2e6",
									backgroundColor: "#fff",
								}}
							>
								<div className="d-flex align-items-start gap-2 flex-wrap">
									<h6
										className="mb-0 flex-grow-1"
										style={{
											fontSize: 14,
											fontWeight: 600,
											color: "#333",
											overflow: "hidden",
											textOverflow: "ellipsis",
											display: "-webkit-box",
											WebkitLineClamp: 2,
											WebkitBoxOrient: "vertical",
										}}
									>
										{selectedSession.firstMessage || "Conversation"}
									</h6>
									<Button
										color="primary"
										size="sm"
										className="d-inline-flex align-items-center gap-1 flex-shrink-0"
										onClick={handleExportSelectedSession}
										disabled={exporting}
									>
										<i className="fa fa-download" aria-hidden="true" />
										Export (XLSX)
									</Button>
									{selectedSession.contactFormSubmitted ? (
										<span
											className="badge rounded-pill flex-shrink-0"
											style={{ backgroundColor: "#198754", fontSize: 11, fontWeight: 600 }}
										>
											Contact form submitted
										</span>
									) : null}
								</div>
								{activeContact ? (
									<div
										className="mt-2 pt-2 border-top small"
										style={{ color: "#495057", lineHeight: 1.5 }}
									>
										<div className="fw-semibold mb-1" style={{ color: "#333" }}>
											Contact details
										</div>
										<div>{contactDisplayName(activeContact)}</div>
										{activeContact.email ? <div>{activeContact.email}</div> : null}
										{activeContact.phone ? <div>{activeContact.phone}</div> : null}
									</div>
								) : null}
							</div>

							{/* Messages */}
							<style>{`
								.cb-conv-msg-text ul { list-style-type: disc; padding-left: 1.5em; margin: 0.5em 0; }
								.cb-conv-msg-text ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.5em 0; }
								.cb-conv-msg-text li { margin: 0.25em 0; font-size: 14px; }
							`}</style>
							<div
								style={{
									flex: 1,
									overflowY: "auto",
									padding: 16,
									backgroundColor: "#f8f9fa",
								}}
							>
								{messagesLoader ? (
									<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
										<div className="d-flex" style={{ justifyContent: "flex-start" }}>
											<div
												style={{
													maxWidth: "75%",
													padding: "10px 14px",
													borderRadius: 12,
													backgroundColor: "#fff",
													border: "1px solid #dee2e6",
												}}
											>
												<Skeleton height={14} width={120} />
											</div>
										</div>
										<div className="d-flex" style={{ justifyContent: "flex-end" }}>
											<div
												style={{
													maxWidth: "75%",
													padding: "10px 14px",
													borderRadius: 12,
													backgroundColor: "#e9ecef",
												}}
											>
												<Skeleton height={14} width={180} />
												<Skeleton height={14} width={160} style={{ marginTop: 6 }} />
												<Skeleton height={14} width={140} style={{ marginTop: 6 }} />
											</div>
										</div>
									</div>
								) : messages.length === 0 ? (
									<div className="text-muted text-center py-5">No messages in this conversation</div>
								) : (
									messages.map((msg, idx) => {
										const isUser = msg.role === "user";
										return (
											<div
												key={idx}
												className="d-flex mb-3"
												style={{
													justifyContent: isUser ? "flex-start" : "flex-end",
												}}
											>
												<div
													style={{
														maxWidth: "75%",
														padding: "10px 14px",
														borderRadius: 12,
														backgroundColor: isUser ? "#fff" : "#e9ecef",
														border: isUser ? "1px solid #dee2e6" : "none",
														boxShadow: isUser ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
													}}
												>
												{isUser ? (
													<div
														style={{ fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
													>
														{msg.content}
													</div>
												) : (
													<>
														<div
															className="cb-conv-msg-text"
															style={{ fontSize: 14, wordBreak: "break-word" }}
															dangerouslySetInnerHTML={{ __html: parseAssistantMarkdown(msg.content || "") }}
														/>
														{msg.retrievalTitles && msg.retrievalTitles.length > 0 ? (
															<div
																className="mt-2 pt-2"
																style={{
																	fontSize: 11,
																	color: "#495057",
																	borderTop: "1px solid rgba(0,0,0,0.06)",
																}}
															>
																<strong style={{ fontWeight: 600 }}>Sources (retrieval):</strong>
																<ul className="mb-0 mt-1 ps-3" style={{ listStyleType: "disc" }}>
																	{msg.retrievalTitles.map((t, ti) => (
																		<li key={ti}>{t}</li>
																	))}
																</ul>
															</div>
														) : null}
													</>
												)}
													<div
														className="text-muted mt-1"
														style={{ fontSize: 11 }}
													>
														{isUser ? "User" : "Assistant"}
														{msg.timestamp &&
															` · ${format(new Date(msg.timestamp), "MM/dd/yyyy h:mm a")}`}
													</div>
												</div>
											</div>
										);
									})
								)}
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default Conversations;
